import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import QuizResultsView from '../components/QuizResultsView';

interface Question {
  id: number;
  question_img_url: string;
  has_multiple_answer: boolean;
  duration: number;
  option_count: number;
  exam_id: number;
  subject_id: number;
  chapter_name?: string;
  chapter_id?: number;
  level?: string;
  topic_id?: number;
  correct_option: string;
  solution?: string;
  question_order?: number;
  user_answer?: string;
  score?: number;
  time_taken?: number;
  attempt_date?: string;
  is_correct?: boolean;
  status?: 'correct' | 'incorrect' | 'unanswered';
  explanation?: string;
  options?: { [key: string]: string };
  question_text?: string;
  correct_answer?: string;
}

interface QuizDetails {
  id: number;
  name: string;
  description: string;
  subject_name?: string;
  exam_name?: string;
  exam_id?: number;
}

interface QuizResultsNavState {
  quizReference: string;
  quizDetails: QuizDetails;
  questions: Question[];
  questionStates: any[];
  totalQuestions: number;
}

const QuizResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  

  // Get quiz results data from navigation state
  const resultsData = location.state as QuizResultsNavState;

  // Redirect to dashboard if no results data
  if (!resultsData) {
    navigate('/dashboard');
    return null;
  }
  
  // Merge questions with user answers from questionStates
  const questionsWithAnswers = resultsData.questions.map((question, index) => {
    const userState = resultsData.questionStates[index] || {};
    
    // Try different possible property names for user answer
    const userAnswer = userState.selectedAnswer || 
                      userState.answer || 
                      userState.selected_option ||
                      userState.user_answer ||
                      userState.selectedOption;
    
    const isAnswered = userState.answered || 
                      userState.isAnswered || 
                      !!userAnswer ||
                      userState.selected_option !== undefined;
    
    return {
      ...question,
      user_answer: userAnswer,
      is_correct: userState.isCorrect,
      time_taken: userState.timeTaken || userState.timeSpent || 0,
      status: (userState.isCorrect ? 'correct' :
              isAnswered ? 'incorrect' : 'unanswered') as 'correct' | 'incorrect' | 'unanswered',
      score: userState.isCorrect ? 4 : isAnswered ? -1 : 0
    };
  });

  // Calculate stats from the merged questionsWithAnswers data
  const totalQuestions = resultsData.totalQuestions;
  const correctAnswers = questionsWithAnswers.filter(q => q.status === 'correct').length;
  const incorrectAnswers = questionsWithAnswers.filter(q => q.status === 'incorrect').length;
  const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  const totalScore = correctAnswers * 4 - incorrectAnswers * 1;

  return (
    <QuizResultsView
      subtitle={<>
        {resultsData.quizDetails?.exam_name && `${resultsData.quizDetails.exam_name.toUpperCase()} - `}
        {resultsData.quizDetails?.subject_name} • {resultsData.quizDetails?.name}
      </>}
      subjectName={resultsData.quizDetails?.subject_name}
      questions={questionsWithAnswers.map(q => ({ ...q, correct_answer: q.correct_option }))}
      totalQuestions={totalQuestions}
      percentage={percentage}
      totalScore={totalScore}
      scoreBreakdown={`+${correctAnswers * 4} correct, -${incorrectAnswers} incorrect`}
      actions={<>
        <Link
          to="/dashboard"
          className="btn btn-outline-primary btn-lg"
          style={{ borderRadius: '15px' }}
        >
          <i className="fas fa-home me-2"></i>
          Back to Dashboard
        </Link>
        
        <Link
          to="/custom-quiz-generator"
          className="btn btn-outline-info btn-lg"
          style={{ borderRadius: '15px' }}
        >
          <i className="fas fa-redo me-2"></i>
          Take Another Quiz
        </Link>
        
        <Link
          to={`/quiz-results-detailed/${resultsData.quizReference}`}
          state={resultsData}
          className="btn btn-outline-success btn-lg"
          style={{ borderRadius: '15px' }}
        >
          <i className="fas fa-chart-line me-2"></i>
          View Detailed Analytics
        </Link>
        
        <button
          className="btn btn-outline-secondary btn-lg"
          style={{ borderRadius: '15px' }}
          onClick={() => window.print()}
        >
          <i className="fas fa-print me-2"></i>
          Print Results
        </button>
      </>}
    />
  );
};

export default QuizResults;
